import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  MainWrapper,
  Container,
  Title,
  Tabs,
  Tab,
  TableWrapper,
  Table,
  Th,
  Tr,
  Td,
  LoaderWrapper,
  LoaderCircle,
  BidSectionContainer,
  BidsTitle,
  BidList,
  BidCard,
  BidHeader,
  BidInfo,
  BidCode,
  StocksGrid,
  StockItem,
  Dot,
  BidFooter,
  PointsText,
  StocksGridWrapper,
  GroupTitle,
  BackArrow
} from "./LeaderboardStyles";

export default function Leaderboard() {
  const getTodayShortName = () =>
    new Date().toLocaleDateString("en-US", { weekday: "short" });

  const [selectedDay, setSelectedDay] = useState(getTodayShortName());
  const [selectedUser, setSelectedUser] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [allBids, setAllBids] = useState([]);
  const [noBidMessage, setNoBidMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showBidSection, setShowBidSection] = useState(false);
  const [formattedDate, setFormattedDate] = useState("");

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Final"];

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        setNoBidMessage("");
        setLeaderboardData([]);
        setSelectedUser(null);

        const res = await axios.get(
          `https://prod-erp.nifty10.in/leaderboard?selectedDay=${selectedDay}`
        );

        const data = res.data?.data || [];
        const isoDate = res.data?.date;

        let formatted = "";
        if (isoDate?.includes("to")) {
          const [start, end] = isoDate.split(" to ");
          const startDate = new Date(start)
            .toLocaleDateString("en-GB")
            .replace(/\//g, "-");
          const endDate = new Date(end)
            .toLocaleDateString("en-GB")
            .replace(/\//g, "-");
          formatted = `${startDate} to ${endDate}`;
        } else if (isoDate) {
          formatted = new Date(isoDate)
            .toLocaleDateString("en-GB")
            .replace(/\//g, "-");
        }

        setFormattedDate(formatted);

        if (data.length === 0) {
          setNoBidMessage(
            res.data?.message || `No Bids Found on ${selectedDay}`
          );
          return;
        }

        // ✅ Handle Final Leaderboard logic
        if (selectedDay === "Final") {
          const finalData = data
            .map((user) => ({
              ...user,
              totalPoints: user.totalPoints || user.score || 0,
            }))
            .sort((a, b) => b.totalPoints - a.totalPoints) // sort highest first
            .map((user, index) => ({
              ...user,
              rank: index + 1,
            }));

          setLeaderboardData(finalData);
          setAllBids([]); // not needed for final
          return;
        }

        // ✅ Flatten all bids across users (for daily)
        const expandedBids = data.flatMap((user) =>
          (user.bids || []).map((bid) => ({
            userId: user.userId,
            user: user.user,
            rank: bid.rank,
            score: bid.score,
            bidCode: bid.bidCode,
            totalBids: user.totalBids,
            allBids: user.bids,
            createdAt: bid.createdAt,
          }))
        );

        setLeaderboardData(data);
        setAllBids(expandedBids);
      } catch (err) {
        setNoBidMessage("Something went wrong while loading leaderboard");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [selectedDay]);

  // ✅ Handle row click (show user + clicked bid on top)
  const handleRowClick = (entry) => {
    if (selectedDay === "Final") return;

    const reordered = [
      entry.allBids.find((b) => b.bidCode === entry.bidCode),
      ...entry.allBids.filter((b) => b.bidCode !== entry.bidCode),
    ];

    setSelectedUser({
      user: entry.user,
      userId: entry.userId,
      bids: reordered,
    });

    if (window.innerWidth <= 768) setShowBidSection(true);
  };

  return (
    <MainWrapper>
      <Container>
        <div style={{ display: "flex", gap: "12px" }}>
          <Title>Leaderboard</Title>
          {formattedDate && (
            <span
              style={{
                padding: "4px 10px",
                borderRadius: "8px",
                fontSize: "1.2rem",
                fontWeight: "500",
                marginTop: "5px",
              }}
            >
              {formattedDate}
            </span>
          )}
        </div>

        <Tabs>
          {days.map((day) => (
            <Tab
              key={day}
              active={day === selectedDay ? "true" : undefined}
              onClick={() => setSelectedDay(day)}
            >
              {day}
            </Tab>
          ))}
        </Tabs>

        {!showBidSection &&
          (loading ? (
            <LoaderWrapper>
              <LoaderCircle />
            </LoaderWrapper>
          ) : (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th>Rank</Th>
                    <Th>User</Th>
                    <Th>Points</Th>
                    {selectedDay !== "Final" && <Th>Bid Code</Th>}
                  </tr>
                </thead>
                <tbody>
                  {noBidMessage ? (
                    <tr>
                      <Td
                        colSpan="5"
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        {noBidMessage}
                      </Td>
                    </tr>
                  ) : selectedDay === "Final" ? (
                    leaderboardData.map((entry, index) => (
                      <Tr key={index} style={{ cursor: "default" }}>
                        <Td>{entry.rank}</Td>
                        <Td title={entry.user}>{entry.user}</Td>
                        <Td>{entry.totalPoints?.toFixed(2)}</Td>
                      </Tr>
                    ))
                  ) : (
                    allBids
                      .sort((a, b) => a.rank - b.rank)
                      .map((entry, index) => (
                        <Tr
                          key={`${entry.userId}-${index}`}
                          onClick={() => handleRowClick(entry)}
                          style={{
                            cursor:
                              selectedDay === "Final" ? "default" : "pointer",
                          }}
                        >
                          <Td>{entry.rank}</Td>
                          <Td title={entry.user}>{entry.user}</Td>
                          <Td>{entry.score?.toFixed(2)}</Td>
                          <Td>{entry.bidCode}</Td>
                        </Tr>
                      ))
                  )}
                </tbody>
              </Table>
            </TableWrapper>
          ))}
      </Container>

      {selectedUser &&
        !loading &&
        (!showBidSection || window.innerWidth <= 768) && selectedDay !== "Final" && (
          <BidSectionContainer>
            {window.innerWidth <= 768 && (
              <BackArrow
                onClick={() => {
                  setShowBidSection(false);
                  setSelectedUser(null);
                }}
              >
                ← Back
              </BackArrow>
            )}
            <BidsTitle>{selectedUser.user} - Bid Details</BidsTitle>
            <BidList>
              {selectedUser?.bids?.map((bid, i) => (
                <BidCard
                  key={bid.bidCode}
                  isBullish={
                    bid.overallSelection === "BULLISH" ? "true" : undefined
                  }
                >
                  <BidHeader>
                    <GroupTitle type="BULLISH">Bullish</GroupTitle>
                    <BidInfo>
                      <BidCode>{bid.bidCode}</BidCode>
                    </BidInfo>
                  </BidHeader>

                  <StocksGridWrapper>
                    <StocksGrid>
                      {bid.companies
                        .filter((s) => s.selectionType === "BULLISH")
                        .map((s, i) => (
                          <StockItem
                            key={`bull-${i}`}
                            isBigBull={
                              s.special === "BIG_BULL" ? "true" : undefined
                            }
                            isBigBear={
                              s.special === "BIG_BEAR" ? "true" : undefined
                            }
                          >
                            <Dot selectionType={s.companyStatus} />
                            {s.name}
                          </StockItem>
                        ))}
                    </StocksGrid>

                    <GroupTitle type="BEARISH">Bearish</GroupTitle>
                    <StocksGrid>
                      {bid.companies
                        .filter((s) => s.selectionType === "BEARISH")
                        .map((s, i) => (
                          <StockItem
                            key={`bear-${i}`}
                            isBigBull={
                              s.special === "BIG_BULL" ? "true" : undefined
                            }
                            isBigBear={
                              s.special === "BIG_BEAR" ? "true" : undefined
                            }
                          >
                            <Dot selectionType={s.companyStatus} />
                            {s.name}
                          </StockItem>
                        ))}
                    </StocksGrid>
                  </StocksGridWrapper>

                  <BidFooter>
                    <PointsText>
                      Total Points: {bid.score?.toFixed(2)}
                    </PointsText>
                    <span>
                      {new Date(bid.createdAt)
                        .toLocaleString("en-GB", {
                          timeZone: "UTC",
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        })
                        .replace(",", "")}
                    </span>


                  </BidFooter>
                </BidCard>
              ))}
            </BidList>
          </BidSectionContainer>
        )}
    </MainWrapper>
  );
}
